/**
 * Certificate Issuance Page - Admin
 *
 * Interface for admins to issue new Halal certificates for approved applications.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/forms/Input';
import type { CertificateType } from '../../types/certificate';

export const CertificateIssuancePage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);

  const [formData, setFormData] = useState({
    certificateNumber: `CERT-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    applicationId: '',
    companyName: '',
    type: 'RESTAURANT' as CertificateType,
    productCategory: '',
    scope: '',
    address: '',
    issueDate: new Date().toISOString().split('T')[0],
    expiryDate: new Date(Date.now() + 730 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 years
    issuedBy: 'JAKIM',
    issuedByOfficer: '',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (window.confirm(t('admin:certificates.issuance.confirmIssue'))) {
      alert(t('admin:certificates.issuance.certificateIssued'));
      window.location.href = '/admin/certificates';
    }
  };

  return (
    <PageLayout
      pageTitle={t('admin:certificates.issuance.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('admin:dashboard.title'), href: '/admin/dashboard' },
        { label: t('admin:certificates.title'), href: '/admin/certificates' },
        { label: t('admin:certificates.issuance.title') },
      ]}
    >
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Certificate Information */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Certificate Information</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:certificates.issuance.certificateNumber')}
                  </label>
                  <Input
                    name="certificateNumber"
                    value={formData.certificateNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('certificateNumber', e.target.value)
                    }
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('admin:certificates.issuance.autoGenerated')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:certificates.issuance.selectApplication')} *
                  </label>
                  <select
                    value={formData.applicationId}
                    onChange={(e) => handleInputChange('applicationId', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select approved application...</option>
                    <option value="APP-001">APP-001 - Kantin Warisan Sdn Bhd</option>
                    <option value="APP-005">APP-005 - Restoran Al-Barakah</option>
                    <option value="APP-009">APP-009 - Food Processing Co</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:certificates.issuance.companyName')} *
                  </label>
                  <Input
                    name="companyName"
                    value={formData.companyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('companyName', e.target.value)
                    }
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certificate Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="RESTAURANT">Restaurant</option>
                    <option value="FOOD_PREMISE">Food Premise</option>
                    <option value="FOOD_PRODUCT">Food Product</option>
                    <option value="FOOD_PREPARATION">Food Preparation</option>
                    <option value="SLAUGHTERHOUSE">Slaughterhouse</option>
                    <option value="LOGISTICS">Logistics</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:certificates.issuance.productCategory')}
                  </label>
                  <Input
                    name="productCategory"
                    value={formData.productCategory}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('productCategory', e.target.value)
                    }
                    placeholder="e.g., Malaysian Cuisine, Bakery Products"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:certificates.issuance.certificationBody')}  *
                  </label>
                  <select
                    value={formData.issuedBy}
                    onChange={(e) => handleInputChange('issuedBy', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="JAKIM">JAKIM</option>
                    <option value="JAIS">JAIS</option>
                    <option value="MAIN">MAIN</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scope of Certification *
                  </label>
                  <textarea
                    value={formData.scope}
                    onChange={(e) => handleInputChange('scope', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe the scope of certification..."
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Premise Address *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Full address of certified premise..."
                    required
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Validity Period */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Validity Period</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:certificates.issuance.issueDate')} *
                  </label>
                  <Input
                    type="date"
                    name="issueDate"
                    value={formData.issueDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('issueDate', e.target.value)
                    }
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin:certificates.issuance.expiryDate')} *
                  </label>
                  <Input
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('expiryDate', e.target.value)
                    }
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Issued By Officer *
                  </label>
                  <Input
                    name="issuedByOfficer"
                    value={formData.issuedByOfficer}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('issuedByOfficer', e.target.value)
                    }
                    placeholder="Name of certifying officer"
                    required
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Actions */}
          <Card>
            <CardBody>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => (window.location.href = '/admin/certificates')}
                  fullWidth
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button type="submit" fullWidth>
                  {t('admin:certificates.issuance.issueCertificate')}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </form>
    </PageLayout>
  );
};
